import './timeregistration-user-registrations-modal.css';

import Modal from '../../modal/modal';
import { UserInfoContext, EmployeesContext } from '../../../context';
import { useMemo, useContext, useState, useCallback } from 'react';
import { currentTimeLogSetting } from '../../../utils/currentTimeLogSetting';
import DANISH_HOLIDAYS from '../../../constants/time/danishHolidays';
import { useLogs } from '../../../logsContext';
import TimeLogEntryWorkHours from './timeregistration-user-registrations-modal-entry-work-hours';
import { isDateLocked } from '../../../utils/isDateLocked';
import { getCompanyStartDate } from '../../../utils/getCompanyStartDate';
import EntryItem from './timeregistration-user-registrations-modal-entry-item';
import EntryLogs from './timeregistration-user-registrations-modal-entry-logs';
import Button from '../../button/button';
import EntryItemAdd from './timeregistration-user-registrations-modal-entry-item-add';
import InputField from '../../input-field/input-field';
import { currentWeek } from '../../../utils/currentWeek';
import { arrayUnion, doc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase-config';
import {
  calculateUserMetrics,
  calculateAllUsersMetrics,
} from '../../../views/timeregistration/timeregistration-utils';

// Helper function to format Date objects to YYYY-MM-DD strings without timezone issues
const formatDateToString = (date) => {
  if (typeof date === 'string') return date;
  if (!(date instanceof Date)) return date;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function TimeRegistrationUserRegistrationsModal({
  isModalOpen,
  setIsModalOpen,
  modalEmployeeId,
  setModalEmployeeId,
  modalDate,
  setModalDate,
}) {
  const { logs, getFilteredLogs } = useLogs();
  const employees = useContext(EmployeesContext);
  const userInfo = useContext(UserInfoContext);
  const [submitting, setSubmitting] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [dateLoading, setDateLoading] = useState(false);

  // Detect week mode by key format (YYYY-UW)
  const weekMatch =
    modalDate && modalDate.match && modalDate.match(/(\d{4})-U(\d{1,2})/);
  const isWeekMode = !!weekMatch;
  const dateObj = useMemo(
    () => (isWeekMode ? null : new Date(modalDate)),
    [modalDate, isWeekMode]
  );

  const employee = employees.find(
    (employee) => employee.id === modalEmployeeId
  );

  // Calculate metrics on-demand for the specific employee and date
  const modalEmployeeMetrics = useMemo(() => {
    if (!modalEmployeeId || !modalDate || !employee) {
      // Return empty/default metrics for new registration mode or when data is missing
      return {
        totalWorkTime: 0,
        normTime: 0,
        overtime: { hours: 0, count: 0, hasNote: false },
        timeOff: {
          hours: 0,
          count: 0,
          hasNote: false,
          timeOffType: '',
          byType: {},
        },
        holidays: { hours: 0, count: 0, hasNote: false },
        sickLeave: { hours: 0, count: 0, hasNote: false },
        work: { hours: 0, count: 0, hasNote: false },
        total: 0,
      };
    }

    // Group logs by user for the calculation function
    const logsByUser = logs.reduce((acc, log) => {
      if (!acc[log.userId]) acc[log.userId] = [];
      acc[log.userId].push(log);
      return acc;
    }, {});

    if (isWeekMode) {
      // For week mode, calculate metrics for the entire week
      const year = Number(weekMatch[1]);
      const week = Number(weekMatch[2]);

      // Find Monday of the week
      const getMonday = (y, w) => {
        const simple = new Date(y, 0, 1 + (w - 1) * 7);
        const dow = simple.getDay();
        const monday = new Date(simple);
        if (dow <= 4) {
          monday.setDate(simple.getDate() - simple.getDay() + 1);
        } else {
          monday.setDate(simple.getDate() + 8 - simple.getDay());
        }
        return monday;
      };

      const monday = getMonday(year, week);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      const weekDateRange = {
        startDate: monday.toISOString().split('T')[0],
        endDate: sunday.toISOString().split('T')[0],
      };

      // Calculate metrics for this week using the same logic as in registrations view
      const weekMetrics = calculateAllUsersMetrics(
        logsByUser,
        [employee],
        userInfo.companyData,
        weekDateRange,
        false
      );

      return (
        weekMetrics[modalEmployeeId] || {
          totalWorkTime: 0,
          normTime: 0,
          overtime: { hours: 0, count: 0, hasNote: false },
          timeOff: {
            hours: 0,
            count: 0,
            hasNote: false,
            timeOffType: '',
            byType: {},
          },
          holidays: { hours: 0, count: 0, hasNote: false },
          sickLeave: { hours: 0, count: 0, hasNote: false },
          work: { hours: 0, count: 0, hasNote: false },
          total: 0,
        }
      );
    } else {
      // For single day mode, calculate metrics for just that day
      const userLogs = logsByUser[modalEmployeeId] || [];
      const userLogsForDate = userLogs.filter((log) => log.date === modalDate);

      return calculateUserMetrics(
        userLogsForDate,
        employee.data,
        userInfo.companyData,
        { startDate: modalDate, endDate: modalDate },
        employee.data.endDate
      );
    }
  }, [
    modalEmployeeId,
    modalDate,
    employee,
    logs,
    userInfo.companyData,
    isWeekMode,
    weekMatch,
  ]);

  const entries = useMemo(() => {
    if (isWeekMode) return [];

    // Ensure modalDate is always a string in YYYY-MM-DD format for comparison
    const dateString = formatDateToString(modalDate);

    const log = logs.find(
      (log) => log.userId === modalEmployeeId && log.date === dateString
    );

    return log?.entries || [];
  }, [logs, modalEmployeeId, modalDate, isWeekMode]);

  const timeLogLogs = useMemo(() => {
    if (isWeekMode) return [];
    const log = logs.find(
      (log) => log.userId === modalEmployeeId && log.date === modalDate
    );
    return log?.logs?.sort((a, b) => b.timestamp.seconds - a.timestamp.seconds);
  }, [logs, modalEmployeeId, modalDate, isWeekMode]);

  // Lock or unlock the date by adding or removing it from the employee's timeLogApprovedWeeks
  const handleLockUnlock = useCallback(async () => {
    setSubmitting(true);

    let weekNumber, year, weekKey;
    if (isWeekMode) {
      // Use Monday of the week
      const yearNum = Number(weekMatch[1]);
      const weekNum = Number(weekMatch[2]);
      const getMonday = (y, w) => {
        const simple = new Date(y, 0, 1 + (w - 1) * 7);
        const dow = simple.getDay();
        const monday = new Date(simple);
        if (dow <= 4) {
          monday.setDate(simple.getDate() - simple.getDay() + 1);
        } else {
          monday.setDate(simple.getDate() + 8 - simple.getDay());
        }
        return monday;
      };
      const monday = getMonday(yearNum, weekNum);
      weekNumber = currentWeek(monday);
      year = monday.getFullYear();
      weekKey = `${weekNumber}-${year}`;
    } else {
      weekNumber = currentWeek(dateObj);
      year = dateObj.getFullYear();
      weekKey = `${weekNumber}-${year}`;
    }

    const addOrRemoveWeekKey =
      employee?.data?.timeLogApprovedWeeks?.includes(weekKey) === true;

    const employeeDocRef = doc(db, 'users', employee.id);

    try {
      await setDoc(
        employeeDocRef,
        {
          timeLogApprovedWeeks: addOrRemoveWeekKey
            ? employee?.data?.timeLogApprovedWeeks.filter(
                (week) => week !== weekKey
              )
            : arrayUnion(weekKey),
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error setting document:', error);
    } finally {
      setSubmitting(false);
    }
  }, [dateObj, employee, isWeekMode, weekMatch]);

  // Handle date change in the modal
  const handleDateChange = useCallback(
    async (e) => {
      let newDate = formatDateToString(e.target.value);

      if (!newDate) return;

      // If no employee is selected yet (we're in employee selector mode), just update the date
      if (!modalEmployeeId) {
        setModalDate(newDate);
        return;
      }

      setDateLoading(true);

      try {
        // Check if we already have logs for this date and employee
        const existingLog = logs.find(
          (log) => log.userId === modalEmployeeId && log.date === newDate
        );

        // If we don't have logs for this date, fetch them
        if (!existingLog) {
          await getFilteredLogs({
            userId: modalEmployeeId,
            startDate: newDate,
            endDate: newDate,
          });
        }

        // Update the modal date - ensure it's always a string
        setModalDate(newDate);
      } catch (error) {
        console.error('Error fetching logs for new date:', error);
        // Still update the date even if fetch fails - user will see empty data
        setModalDate(newDate);
      } finally {
        setDateLoading(false);
      }
    },
    [modalEmployeeId, logs, getFilteredLogs, setModalDate]
  );

  if (!modalDate) {
    return null;
  }

  // If no employee is selected, show employee selector
  if (!modalEmployeeId) {
    return (
      <Modal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        header="Ny registrering"
        className="timeregistration-user-registrations-modal"
        customWidth={'600px'}
        content={
          <div className="timeregistration-user-registrations-modal-overview">
            <p>
              Som admin kan du oprette en tidsregistrering manuelt på en
              medarbejder. Vælg dato og medarbejder for at fortsætte.
            </p>
            <div className="timeregistration-user-registrations-modal-overview-row">
              <div className="timeregistration-user-registrations-modal-overview-row-label">
                Dato
              </div>
              <div className="timeregistration-user-registrations-modal-overview-row-value">
                <InputField
                  type="date"
                  label="Dato for registrering"
                  value={formatDateToString(modalDate)}
                  onChange={handleDateChange}
                  datePickerFixed
                  datePickerPosition={'bottom'}
                  disabled={dateLoading}
                />
              </div>
            </div>
            <div className="timeregistration-user-registrations-modal-overview-row">
              <div className="timeregistration-user-registrations-modal-overview-row-label">
                Medarbejder
              </div>
              <div className="timeregistration-user-registrations-modal-overview-row-value">
                <InputField
                  key={'filter-employee'}
                  label={'Medarbejder'}
                  className={'filter-employee'}
                  type="select2"
                  name={'employee'}
                  options={employees
                    .filter((emp) => emp?.data?.settings?.mobileApp?.access)
                    .filter(
                      (emp) => !emp?.data?.settings?.timeRegistration?.exempt
                    )
                    .filter((emp) => emp?.data?.employmentStatus !== 'inactive')
                    .map((employee) => ({
                      value: employee.id,
                      label: `${employee.data.firstName} ${employee.data.lastName}`,
                      target: {
                        value: employee.id,
                        name: 'employee',
                      },
                    }))}
                  placeholder="- Vælg -"
                  value={employees.find((e) => e.id === modalEmployeeId)}
                  onChange={async (selectedOption) => {
                    const emp = employees.find(
                      (e) => e.id === selectedOption.value
                    );
                    setDateLoading(true);
                    try {
                      // Ensure modalDate is in proper string format
                      const dateString = formatDateToString(modalDate);

                      // Check if we already have logs for this date and employee
                      const existingLog = logs.find(
                        (log) =>
                          log.userId === emp.id && log.date === dateString
                      );

                      // If we don't have logs for this date, fetch them
                      if (!existingLog) {
                        await getFilteredLogs({
                          userId: emp.id,
                          startDate: dateString,
                          endDate: dateString,
                        });
                      }

                      setModalEmployeeId(emp.id);
                    } catch (error) {
                      console.error(
                        'Error fetching logs for selected employee:',
                        error
                      );
                      setModalEmployeeId(emp.id); // Still set the employee even if fetch fails
                    } finally {
                      setDateLoading(false);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        }
      />
    );
  }

  if (!employee) {
    return null;
  }

  const timeLogSetting =
    !isWeekMode &&
    currentTimeLogSetting(userInfo.companyData, employee?.data, dateObj);

  let date, weekTitle, weekRange;
  if (isWeekMode) {
    // modalDate is YYYY-UW
    const year = weekMatch[1];
    const week = weekMatch[2];
    weekTitle = `Uge ${week}`;
    // Find the date range for this week
    // Find Monday of the week
    const getMonday = (y, w) => {
      const simple = new Date(y, 0, 1 + (w - 1) * 7);
      const dow = simple.getDay();
      const monday = new Date(simple);
      if (dow <= 4) {
        monday.setDate(simple.getDate() - simple.getDay() + 1);
      } else {
        monday.setDate(simple.getDate() + 8 - simple.getDay());
      }
      return monday;
    };
    const monday = getMonday(Number(year), Number(week));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    weekRange = `${monday.toLocaleDateString(
      'da-DK'
    )} - ${sunday.toLocaleDateString('da-DK')}`;
    date = weekRange;
  } else {
    date = dateObj.toLocaleDateString('da-DK', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  // Check if the day is a Danish holiday
  const isHoliday =
    !isWeekMode &&
    DANISH_HOLIDAYS.filter((holiday) => holiday.value === date)[0];

  // For week mode, lock status is for the week (use Monday as reference)
  const locked = isWeekMode
    ? isDateLocked(
        (() => {
          const year = weekMatch[1];
          const week = weekMatch[2];
          const monday = new Date(Number(year), 0, 1 + (Number(week) - 1) * 7);
          const dow = monday.getDay();
          if (dow <= 4) {
            monday.setDate(monday.getDate() - monday.getDay() + 1);
          } else {
            monday.setDate(monday.getDate() + 8 - monday.getDay());
          }
          return monday;
        })(),
        employee?.data?.timeLogApprovedWeeks || [],
        employee?.data?.startDate,
        getCompanyStartDate(userInfo.companyData)
      )
    : isDateLocked(
        dateObj,
        employee?.data?.timeLogApprovedWeeks || [],
        employee?.data?.startDate,
        getCompanyStartDate(userInfo.companyData)
      );

  return (
    <>
      <Modal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        header={isWeekMode ? weekTitle : date}
        className="timeregistration-user-registrations-modal"
        customWidth={'600px'}
        content={
          <>
            <div className="timeregistration-user-registrations-modal-overview">
              <div className="timeregistration-user-registrations-modal-overview-row">
                <div className="timeregistration-user-registrations-modal-overview-row-label">
                  Status
                </div>
                <div className="timeregistration-user-registrations-modal-overview-row-value">
                  {locked ? 'Godkendt' : 'Åben'}
                </div>
                <div className="timeregistration-user-registrations-modal-overview-row-value timeregistration-user-registrations-modal-overview-row-value--options">
                  {locked ? (
                    <Button
                      className="small"
                      buttonStyle="text-link"
                      onClick={handleLockUnlock}
                      disabled={submitting}
                    >
                      {submitting
                        ? 'Åbner ugen op...'
                        : `Åben uge ${
                            isWeekMode ? weekMatch[2] : currentWeek(dateObj)
                          } op`}
                    </Button>
                  ) : (
                    <Button
                      className="small"
                      buttonStyle="text-link"
                      onClick={handleLockUnlock}
                      disabled={submitting}
                    >
                      {submitting
                        ? 'Godkender ugen...'
                        : `Godkend uge ${
                            isWeekMode ? weekMatch[2] : currentWeek(dateObj)
                          }`}
                    </Button>
                  )}
                </div>
              </div>
              <div className="timeregistration-user-registrations-modal-overview-row">
                <div className="timeregistration-user-registrations-modal-overview-row-label">
                  Dato
                </div>
                <div className="timeregistration-user-registrations-modal-overview-row-value">
                  {isWeekMode ? (
                    weekRange
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <InputField
                        type="date"
                        label="Dato"
                        value={formatDateToString(modalDate)}
                        datePickerPosition="bottom"
                        datePickerFixed={true}
                        onChange={handleDateChange}
                        disabled={dateLoading || locked}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="timeregistration-user-registrations-modal-overview-row">
                <div className="timeregistration-user-registrations-modal-overview-row-label">
                  Medarbejder
                </div>
                <div className="timeregistration-user-registrations-modal-overview-row-value">
                  {employee.data.firstName} {employee.data.lastName}
                </div>
              </div>
              {/* Normtid row removed in week mode */}
              {!timeLogSetting.isPaidHourly && !isWeekMode && (
                <div className="timeregistration-user-registrations-modal-overview-row">
                  <div className="timeregistration-user-registrations-modal-overview-row-label">
                    Normtid
                  </div>
                  <div className="timeregistration-user-registrations-modal-overview-row-value">
                    {timeLogSetting &&
                      !timeLogSetting.isPaidHourly &&
                      (isHoliday ? (
                        isHoliday.label
                      ) : timeLogSetting.notWorking ? (
                        'Fri'
                      ) : (
                        <>
                          {timeLogSetting.from
                            .toDate()
                            .toLocaleTimeString('da-DK', {
                              timeStyle: 'short',
                            })}{' '}
                          -{' '}
                          {timeLogSetting.end
                            .toDate()
                            .toLocaleTimeString('da-DK', {
                              timeStyle: 'short',
                            })}
                        </>
                      ))}
                  </div>
                </div>
              )}
            </div>
            <div className="timeregistration-user-registrations-modal-entries">
              {isWeekMode ? (
                <div className="timeregistration-user-registrations-modal-week-summary">
                  <div className="row">
                    <div className="type">
                      <div className="label">Arbejde u.f. normtid</div>
                      {modalEmployeeMetrics?.overtime?.count > 0 && (
                        <div className="amount">
                          {modalEmployeeMetrics?.overtime?.count}{' '}
                          {`registrering${
                            modalEmployeeMetrics?.overtime?.count > 1
                              ? 'er'
                              : ''
                          }`}
                        </div>
                      )}
                    </div>
                    <div className="value">
                      {modalEmployeeMetrics?.overtime?.hours?.toLocaleString(
                        'da-DK',
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      ) || '0,00'}{' '}
                      {`time${
                        modalEmployeeMetrics?.overtime?.hours > 1 ? 'r' : ''
                      }`}
                    </div>
                  </div>
                  <div className="row">
                    <div className="type">
                      <div className="label">Fri</div>
                      {modalEmployeeMetrics?.timeOff?.count > 0 && (
                        <div className="amount">
                          {modalEmployeeMetrics?.timeOff?.count}{' '}
                          {`registrering${
                            modalEmployeeMetrics?.timeOff?.count > 1 ? 'er' : ''
                          }`}
                        </div>
                      )}
                    </div>
                    {modalEmployeeMetrics?.normTime !== 0 && (
                      <div className="percentage">
                        {(
                          (modalEmployeeMetrics?.timeOff?.hours /
                            modalEmployeeMetrics?.normTime) *
                          100
                        ).toLocaleString('da-DK', {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                        %
                      </div>
                    )}
                    <div className="value">
                      {modalEmployeeMetrics?.timeOff?.hours?.toLocaleString(
                        'da-DK',
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      ) || '0,00'}{' '}
                      {`time${
                        modalEmployeeMetrics?.timeOff?.hours > 1 ? 'r' : ''
                      }`}
                    </div>
                  </div>
                  <div className="row">
                    <div className="type">
                      <div className="label">Ferie</div>
                      {modalEmployeeMetrics?.holidays?.count > 0 && (
                        <div className="amount">
                          {modalEmployeeMetrics?.holidays?.count}{' '}
                          {`registrering${
                            modalEmployeeMetrics?.holidays?.count > 1
                              ? 'er'
                              : ''
                          }`}
                        </div>
                      )}
                    </div>
                    {modalEmployeeMetrics?.normTime !== 0 && (
                      <div className="percentage">
                        {(
                          (modalEmployeeMetrics?.holidays?.hours /
                            modalEmployeeMetrics?.normTime) *
                          100
                        ).toLocaleString('da-DK', {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                        %
                      </div>
                    )}
                    <div className="value">
                      {modalEmployeeMetrics?.holidays?.hours?.toLocaleString(
                        'da-DK',
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      ) || '0,00'}{' '}
                      {`time${
                        modalEmployeeMetrics?.holidays?.hours > 1 ? 'r' : ''
                      }`}
                    </div>
                  </div>
                  <div className="row">
                    <div className="type">
                      <div className="label">Sygdom</div>
                      {modalEmployeeMetrics?.sickLeave?.count > 0 && (
                        <div className="amount">
                          {modalEmployeeMetrics?.sickLeave?.count}{' '}
                          {`registrering${
                            modalEmployeeMetrics?.sickLeave?.count > 1
                              ? 'er'
                              : ''
                          }`}
                        </div>
                      )}
                    </div>
                    {modalEmployeeMetrics?.normTime !== 0 && (
                      <div className="percentage">
                        {(
                          (modalEmployeeMetrics?.sickLeave?.hours /
                            modalEmployeeMetrics?.normTime) *
                          100
                        ).toLocaleString('da-DK', {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                        %
                      </div>
                    )}
                    <div className="value">
                      {modalEmployeeMetrics?.sickLeave?.hours?.toLocaleString(
                        'da-DK',
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      ) || '0,00'}{' '}
                      {`time${
                        modalEmployeeMetrics?.sickLeave?.hours > 1 ? 'r' : ''
                      }`}
                    </div>
                  </div>

                  <div className="row">
                    <div className="type">
                      <div className="label">Arbejdstid</div>
                      {modalEmployeeMetrics?.work?.count > 0 && (
                        <div className="amount">
                          {modalEmployeeMetrics?.work?.count}{' '}
                          {`registrering${
                            modalEmployeeMetrics?.work?.count > 1 ? 'er' : ''
                          }`}
                        </div>
                      )}
                    </div>
                    {modalEmployeeMetrics?.normTime !== 0 && (
                      <div className="percentage">
                        {(
                          (modalEmployeeMetrics?.work?.hours /
                            modalEmployeeMetrics?.normTime) *
                          100
                        ).toLocaleString('da-DK', {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                        %
                      </div>
                    )}
                    <div className="value">
                      {modalEmployeeMetrics?.work?.hours?.toLocaleString(
                        'da-DK',
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      ) || '0,00'}{' '}
                      {`time${
                        modalEmployeeMetrics?.work?.hours > 1 ? 'r' : ''
                      }`}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {!timeLogSetting.isPaidHourly && (
                    <TimeLogEntryWorkHours
                      date={dateObj}
                      timeLogSetting={timeLogSetting}
                    />
                  )}
                  {entries.map((entry, index) => (
                    <EntryItem
                      key={index}
                      entry={entry}
                      modalEmployeeId={modalEmployeeId}
                      modalDate={modalDate}
                      timeOffTypes={userInfo.companyData.timeOffTypes}
                    />
                  ))}
                  {addMode && (
                    <EntryItemAdd
                      modalEmployeeId={modalEmployeeId}
                      modalDate={modalDate}
                      timeOffTypes={userInfo.companyData.timeOffTypes}
                      setAddMode={setAddMode}
                    />
                  )}
                  <div className="timeregistration-user-registrations-modal-entries-add">
                    <Button
                      buttonStyle="text-link"
                      onClick={() => setAddMode(true)}
                    >
                      Ny registrering
                    </Button>
                  </div>
                  {timeLogLogs?.length > 0 && <EntryLogs logs={timeLogLogs} />}
                </>
              )}
            </div>
          </>
        }
        bottom={
          <div className="timeregistration-user-registrations-modal-bottom">
            <div className="timeregistration-user-registrations-modal-bottom-row">
              <div>Total</div>
              <div>
                {modalEmployeeMetrics?.total?.toLocaleString('da-DK', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) || '0,00'}{' '}
                timer
              </div>
            </div>
            <div className="timeregistration-user-registrations-modal-bottom-row">
              <div>Heraf arbejdstid</div>
              <div>
                {modalEmployeeMetrics?.totalWorkTime?.toLocaleString('da-DK', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) || '0,00'}{' '}
                timer
              </div>
            </div>
            {!timeLogSetting.isPaidHourly && (
              <div className="timeregistration-user-registrations-modal-bottom-row">
                <div>Normtid for perioden</div>
                <div>
                  {modalEmployeeMetrics?.normTime?.toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) || '0,00'}{' '}
                  timer
                </div>
              </div>
            )}
          </div>
        }
      />
    </>
  );
}

export default TimeRegistrationUserRegistrationsModal;
